import React from 'react';

/**
 * Allows you to close a menu when user clicks outside of the node's family tree or presses escape.
 *
 * @param ref A reference to a DOM node. Should be the most senior node that you you want to affect.
 * @param isMenuOpen This determines if the menu is open or closed.
 * @param callback This determines behaviour on successful click or keydown.
 */
function useCloseMenu(
	ref: React.MutableRefObject<HTMLDivElement>,
	isMenuOpen: boolean,
	callback: () => void,
): void {
	const handleClick = React.useCallback(
		({ target }: MouseEvent): void => {
			if (!ref.current?.contains(target as Node) && isMenuOpen) {
				callback();
			}
		},
		[ref, isMenuOpen, callback],
	);

	const handleKeydown = React.useCallback(
		({ key }: KeyboardEvent): void => {
			if (key === ('Escape' || 'Esc') && isMenuOpen) {
				callback();
			}
		},
		[isMenuOpen, callback],
	);

	React.useEffect(() => {
		window.addEventListener('click', handleClick, { capture: true });
		window.addEventListener('keydown', handleKeydown, { capture: true });
		return () => {
			window.removeEventListener('click', handleClick, { capture: true });
			window.addEventListener('keydown', handleKeydown, { capture: true });
		};
	}, [handleClick, handleKeydown]);
}

export default useCloseMenu;
